import { simulationInputFingerprint, toleranceInputFingerprint } from './candidateEvidence'
import { defaultState } from './defaults'
import type {
  AppState,
  CalibrationDataset,
  CalibrationFit,
  CandidateLens,
  MonteCarloResult,
  ProjectDocumentV1,
  ProjectResultRecord,
  SimulationResult,
} from './types'

export const PROJECT_SCHEMA = 'lenticular-display-lab/project' as const
export const PROJECT_VERSION = 1 as const
export const LEGACY_STATE_KEY = 'lenticular-display-lab/state/v4'

const DATABASE_NAME = 'lenticular-display-lab'
const DATABASE_VERSION = 1
const STORE_NAME = 'projects'
const CURRENT_PROJECT_KEY = 'current'

type UnknownRecord = Record<string, unknown>

export interface ProjectSnapshot {
  state: AppState
  simulation: ProjectResultRecord<SimulationResult> | null
  tolerance: ProjectResultRecord<MonteCarloResult> | null
  calibrationDataset: CalibrationDataset | null
  calibrationFit: CalibrationFit | null
  calibrationFitFingerprint: string | null
}

export interface ProjectParseResult {
  project: ProjectDocumentV1 | null
  errors: string[]
}

export interface ProjectStorageBackend {
  read(): Promise<unknown | null>
  write(project: ProjectDocumentV1): Promise<void>
}

export interface ProjectLoadResult {
  project: ProjectDocumentV1
  source: 'indexeddb' | 'legacy' | 'default'
  warning: string | null
  writable: boolean
}

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const isNullableFinite = (value: unknown): value is number | null =>
  value === null || isFiniteNumber(value)

const isFiniteArray = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every(isFiniteNumber)

const isNonnegativeFiniteArray = (value: unknown): value is number[] =>
  isFiniteArray(value) && value.every((item) => item >= 0)

const isNonnegativeInteger = (value: unknown): value is number =>
  isFiniteNumber(value) && Number.isInteger(value) && value >= 0

const isInteger = (value: unknown): value is number =>
  isFiniteNumber(value) && Number.isInteger(value)

const isPositiveInteger = (value: unknown): value is number =>
  isNonnegativeInteger(value) && value > 0

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string')

function isJsonData(value: unknown, depth = 0): boolean {
  if (depth > 30) return false
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true
  if (isFiniteNumber(value)) return true
  if (Array.isArray(value)) return value.every((item) => isJsonData(item, depth + 1))
  if (!isRecord(value)) return false
  return Object.values(value).every((item) => isJsonData(item, depth + 1))
}

function followsTemplate(value: unknown, template: unknown, path: string, errors: string[]): void {
  if (template === null) {
    if (!isNullableFinite(value)) errors.push(`${path} must be a finite number or null.`)
    return
  }
  if (typeof template === 'number') {
    if (!isFiniteNumber(value)) errors.push(`${path} must be a finite number.`)
    return
  }
  if (typeof template === 'string' || typeof template === 'boolean') {
    if (typeof value !== typeof template) errors.push(`${path} must be ${typeof template}.`)
    return
  }
  if (Array.isArray(template)) {
    if (!Array.isArray(value)) {
      errors.push(`${path} must be an array.`)
      return
    }
    if (template.length) {
      value.forEach((item, index) => followsTemplate(item, template[0], `${path}[${index}]`, errors))
    }
    return
  }
  if (!isRecord(template) || !isRecord(value)) {
    errors.push(`${path} must be an object.`)
    return
  }
  Object.entries(template).forEach(([key, childTemplate]) => {
    if (!(key in value)) {
      errors.push(`${path}.${key} is required.`)
      return
    }
    followsTemplate(value[key], childTemplate, `${path}.${key}`, errors)
  })
}

function oneOf(value: unknown, choices: readonly string[], path: string, errors: string[]): void {
  if (typeof value !== 'string' || !choices.includes(value)) {
    errors.push(`${path} has an unsupported value.`)
  }
}

const isUnitInterval = (value: unknown): value is number =>
  isFiniteNumber(value) && value >= 0 && value <= 1

const isValidTimestamp = (value: unknown): value is string =>
  typeof value === 'string' && Number.isFinite(Date.parse(value))

function validateCandidateSimulationSummary(
  value: unknown,
  candidate: CandidateLens,
  path: string,
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object.`)
    return
  }
  if (value.candidateId !== candidate.id) {
    errors.push(`${path}.candidateId must match its candidate ID.`)
  }
  const contract = typeof value.inputFingerprint === 'string'
    ? simulationFingerprintContract(value.inputFingerprint)
    : null
  if (!contract) {
    errors.push(`${path}.inputFingerprint must be a valid simulation fingerprint.`)
  } else {
    const expectedModel = contract.mode === 'thin'
      ? 'paraxial thin-lens simulation'
      : 'geometric thick-lens ray trace'
    if (value.model !== expectedModel) {
      errors.push(`${path}.model must match its evidence fingerprint.`)
    }
  }
  if (!isUnitInterval(value.crosstalkMax)) {
    errors.push(`${path}.crosstalkMax must be a finite number from 0 to 1.`)
  }
  if (!isUnitInterval(value.throughputMin)) {
    errors.push(`${path}.throughputMin must be a finite number from 0 to 1.`)
  }
  if (!isFiniteNumber(value.zoneCenterErrorMaxMm) || value.zoneCenterErrorMaxMm < 0) {
    errors.push(`${path}.zoneCenterErrorMaxMm must be a nonnegative finite number.`)
  }
  if (!isValidTimestamp(value.runAt)) {
    errors.push(`${path}.runAt must be a valid timestamp.`)
  }
}

function validateCandidateMonteCarloSummary(
  value: unknown,
  candidate: CandidateLens,
  path: string,
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object.`)
    return
  }
  if (value.model !== 'Monte Carlo tolerance result') {
    errors.push(`${path}.model is invalid.`)
  }
  if (value.candidateId !== candidate.id) {
    errors.push(`${path}.candidateId must match its candidate ID.`)
  }
  if (
    typeof value.inputFingerprint !== 'string' ||
    !toleranceFingerprintContract(value.inputFingerprint)
  ) {
    errors.push(`${path}.inputFingerprint must be a valid tolerance fingerprint.`)
  }
  if (!isUnitInterval(value.successRate)) {
    errors.push(`${path}.successRate must be a finite number from 0 to 1.`)
  }
  if (!isPositiveInteger(value.trials)) {
    errors.push(`${path}.trials must be a positive integer.`)
  }
  if (!isInteger(value.seed)) {
    errors.push(`${path}.seed must be an integer.`)
  }
  if (!isValidTimestamp(value.runAt)) {
    errors.push(`${path}.runAt must be a valid timestamp.`)
  }
}

export function validateAppState(value: unknown): string[] {
  const errors: string[] = []
  followsTemplate(value, defaultState, 'state', errors)
  if (!isRecord(value)) return errors
  if (!isJsonData(value)) errors.push('state must contain only finite JSON data.')

  const lcd = isRecord(value.lcd) ? value.lcd : null
  const view = isRecord(value.view) ? value.view : null
  const lens = isRecord(value.lens) ? value.lens : null
  const simulationSettings = isRecord(value.simulationSettings) ? value.simulationSettings : null
  const toleranceSettings = isRecord(value.toleranceSettings) ? value.toleranceSettings : null

  if (lcd) {
    oneOf(lcd.sourceMode, ['pixel-columns', 'rgb-stripe', 'custom'], 'state.lcd.sourceMode', errors)
    oneOf(lcd.apertureMode, ['width', 'fill-factor'], 'state.lcd.apertureMode', errors)
    oneOf(lcd.emissionModel, ['flat', 'lambertian', 'gaussian'], 'state.lcd.emissionModel', errors)
  }
  if (view) {
    oneOf(view.centerMode, ['even', 'custom', 'two-view-eyes'], 'state.view.centerMode', errors)
    if (!isFiniteArray(view.customCentersMm)) {
      errors.push('state.view.customCentersMm must contain only finite numbers.')
    }
  }
  if (lens) oneOf(lens.mode, ['thin', 'thick'], 'state.lens.mode', errors)
  if (isRecord(lens?.thin)) {
    oneOf(
      lens.thin.focalReference,
      ['effective-at-plane', 'back-from-plane', 'vendor-unspecified'],
      'state.lens.thin.focalReference',
      errors,
    )
  }
  if (Array.isArray(value.layers)) {
    value.layers.forEach((layer, index) => {
      if (isRecord(layer)) {
        oneOf(layer.distribution, ['normal', 'uniform'], `state.layers[${index}].distribution`, errors)
      }
    })
  }
  if (Array.isArray(value.candidates)) {
    const candidateIds = new Set<string>()
    value.candidates.forEach((candidate, index) => {
      if (!isRecord(candidate)) return
      const path = `state.candidates[${index}]`
      if (typeof candidate.id !== 'string' || candidate.id.trim().length === 0) {
        errors.push(`${path}.id must be a nonempty string.`)
      } else if (candidateIds.has(candidate.id)) {
        errors.push(`${path}.id duplicates another candidate ID.`)
      } else {
        candidateIds.add(candidate.id)
      }
      oneOf(candidate.mode, ['thin', 'thick'], `state.candidates[${index}].mode`, errors)
      oneOf(
        candidate.focalReference,
        ['effective-at-plane', 'back-from-plane', 'vendor-unspecified'],
        `state.candidates[${index}].focalReference`,
        errors,
      )
      if ('simulated' in candidate) {
        validateCandidateSimulationSummary(
          candidate.simulated,
          candidate as unknown as CandidateLens,
          `${path}.simulated`,
          errors,
        )
      }
      if ('monteCarlo' in candidate) {
        validateCandidateMonteCarloSummary(
          candidate.monteCarlo,
          candidate as unknown as CandidateLens,
          `${path}.monteCarlo`,
          errors,
        )
      }
    })
  }
  if (simulationSettings) {
    oneOf(simulationSettings.preset, ['preview', 'balanced', 'high'], 'state.simulationSettings.preset', errors)
  }
  if (toleranceSettings) {
    oneOf(
      toleranceSettings.mode,
      ['analytical-paraxial', 'geometric-ray'],
      'state.toleranceSettings.mode',
      errors,
    )
    oneOf(
      toleranceSettings.defaultDistribution,
      ['normal', 'uniform'],
      'state.toleranceSettings.defaultDistribution',
      errors,
    )
  }
  return errors
}

function parseFingerprint(value: string): UnknownRecord | null {
  try {
    const parsed = JSON.parse(value) as unknown
    return isRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}

function sameFiniteArray(left: number[], right: number[]): boolean {
  return left.length === right.length && left.every((item, index) => item === right[index])
}

function isStrictlyIncreasing(values: number[]): boolean {
  return values.every((value, index) => index === 0 || value > values[index - 1])
}

function simulationFingerprintContract(fingerprint: string): {
  views: number
  mode: 'thin' | 'thick'
  settings: UnknownRecord
} | null {
  const parsed = parseFingerprint(fingerprint)
  if (!parsed || !isRecord(parsed.view) || !isRecord(parsed.lens) || !isRecord(parsed.settings)) return null
  if (!isPositiveInteger(parsed.view.views)) return null
  if (parsed.lens.mode !== 'thin' && parsed.lens.mode !== 'thick') return null
  const settingsErrors: string[] = []
  followsTemplate(parsed.settings, defaultState.simulationSettings, 'fingerprint.settings', settingsErrors)
  return settingsErrors.length
    ? null
    : { views: parsed.view.views, mode: parsed.lens.mode, settings: parsed.settings }
}

function isSimulationResult(value: unknown, fingerprint: string): value is SimulationResult {
  if (!isRecord(value)) return false
  const contract = simulationFingerprintContract(fingerprint)
  if (!contract) return false
  if (
    value.model !== 'paraxial thin-lens simulation' &&
    value.model !== 'geometric thick-lens ray trace'
  ) return false
  const expectedModel = contract.mode === 'thin'
    ? 'paraxial thin-lens simulation'
    : 'geometric thick-lens ray trace'
  if (value.model !== expectedModel || JSON.stringify(value.settings) !== JSON.stringify(contract.settings)) return false
  if (!Array.isArray(value.intensity) || value.intensity.length !== contract.views) return false
  const settings = value.settings
  if (!isRecord(settings) || !isPositiveInteger(settings.viewerBins)) return false
  let sharedX: number[] | null = null
  const intensityValid = value.intensity.every((series, index) => {
    if (!isRecord(series) || series.view !== index) return false
    if (!isFiniteArray(series.xMm) || !isNonnegativeFiniteArray(series.intensity)) return false
    if (series.xMm.length !== settings.viewerBins || !isStrictlyIncreasing(series.xMm)) return false
    if (sharedX && !sameFiniteArray(series.xMm, sharedX)) return false
    sharedX ??= series.xMm
    return series.xMm.length === series.intensity.length &&
      isNullableFinite(series.peakMm) &&
      isNullableFinite(series.centroidMm) &&
      isNullableFinite(series.fwhmMm) &&
      (series.fwhmMm === null || series.fwhmMm >= 0)
  })
  const matrixValid = Array.isArray(value.transferMatrix) &&
    value.transferMatrix.length === contract.views &&
    value.transferMatrix.every((row) =>
      isNonnegativeFiniteArray(row) && row.length === contract.views,
    )
  const raysValid = Array.isArray(value.rays) && value.rays.every((ray) => {
    if (!isRecord(ray) || !isNonnegativeInteger(ray.view) || ray.view >= contract.views) return false
    if (!isFiniteNumber(ray.weight) || ray.weight < 0) return false
    if (!['transmitted', 'tir', 'missed', 'unreachable'].includes(String(ray.outcome))) return false
    return Array.isArray(ray.points) && ray.points.every((point) =>
      isRecord(point) && isFiniteNumber(point.x) && isFiniteNumber(point.y),
    )
  })
  const discardedValue = value.discarded
  const discarded = isRecord(discardedValue) &&
    ['total', 'tir', 'unreachable', 'missedSurface'].every((key) => isNonnegativeInteger(discardedValue[key])) &&
    ['tir', 'unreachable', 'missedSurface'].every((key) =>
      (discardedValue[key] as number) <= (discardedValue.total as number),
    )
  const settingsErrors: string[] = []
  followsTemplate(value.settings, defaultState.simulationSettings, 'result.settings', settingsErrors)
  const targetVectors = [
    value.desiredSourceByTarget,
    value.crosstalk,
    value.desiredTransmission,
    value.targetCentersMm,
    value.zoneCenterErrorsMm,
  ]
  const vectorsValid = targetVectors.every((vector) =>
    isFiniteArray(vector) && vector.length === contract.views,
  )
  const desiredSourcesValid = Array.isArray(value.desiredSourceByTarget) &&
    value.desiredSourceByTarget.every((source) =>
      isNonnegativeInteger(source) && source < contract.views,
    )
  const boundedCrosstalk = Array.isArray(value.crosstalk) &&
    value.crosstalk.every((item) => isFiniteNumber(item) && item >= 0 && item <= 1)
  const nonnegativeTransmission = isNonnegativeFiniteArray(value.desiredTransmission)
  return intensityValid && matrixValid && raysValid && Boolean(discarded) && !settingsErrors.length &&
    vectorsValid && desiredSourcesValid && boundedCrosstalk && nonnegativeTransmission &&
    isFiniteNumber(value.relativeThroughput) && value.relativeThroughput >= 0 &&
    value.relativeThroughput <= 1 && isStringArray(value.warnings) &&
    isFiniteNumber(value.elapsedMs) && value.elapsedMs >= 0 && isJsonData(value)
}

function isPercentiles(value: unknown): boolean {
  return isRecord(value) &&
    isNullableFinite(value.p05) && isNullableFinite(value.p50) && isNullableFinite(value.p95)
}

function percentile(values: number[], probability: number): number | null {
  if (!values.length) return null
  const sorted = [...values].sort((left, right) => left - right)
  const position = (sorted.length - 1) * probability
  const lower = Math.floor(position)
  const upper = Math.ceil(position)
  if (lower === upper) return sorted[lower]
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower)
}

function percentilesMatch(value: unknown, distribution: number[]): boolean {
  if (!isPercentiles(value) || !isRecord(value)) return false
  return value.p05 === percentile(distribution, 0.05) &&
    value.p50 === percentile(distribution, 0.5) &&
    value.p95 === percentile(distribution, 0.95)
}

function toleranceFingerprintContract(fingerprint: string): UnknownRecord | null {
  const parsed = parseFingerprint(fingerprint)
  if (!parsed || typeof parsed.simulationFingerprint !== 'string' || !isRecord(parsed.settings)) return null
  if (!simulationFingerprintContract(parsed.simulationFingerprint)) return null
  const settingsErrors: string[] = []
  followsTemplate(parsed.settings, defaultState.toleranceSettings, 'fingerprint.settings', settingsErrors)
  return settingsErrors.length ? null : parsed.settings
}

function isMonteCarloResult(value: unknown, fingerprint: string): value is MonteCarloResult {
  if (!isRecord(value)) return false
  const settings = toleranceFingerprintContract(fingerprint)
  if (!settings) return false
  if (value.model !== 'Monte Carlo tolerance result') return false
  if (value.evaluationModel !== 'analytical/paraxial' && value.evaluationModel !== 'geometric ray') return false
  if (!isNonnegativeInteger(value.trials) || !isNonnegativeInteger(value.validTrials) ||
    !isNonnegativeInteger(value.invalidTrials)) return false
  if (value.validTrials + value.invalidTrials !== value.trials) return false
  const maximumTrials = settings.mode === 'geometric-ray' ? 2000 : 10000
  const expectedTrials = Math.max(10, Math.min(maximumTrials, Math.round(settings.trials as number)))
  if (value.trials !== expectedTrials || value.seed !== settings.seed) return false
  const expectedModel = settings.mode === 'geometric-ray' ? 'geometric ray' : 'analytical/paraxial'
  if (value.evaluationModel !== expectedModel) return false
  if (!isFiniteNumber(value.successRate) || value.successRate < 0 || value.successRate > 1) return false
  const successCount = value.successRate * value.trials
  if (value.trials === 0 ? value.successRate !== 0 :
    Math.abs(successCount - Math.round(successCount)) > 1e-9 || successCount > value.validTrials) return false
  const percentileKeys = ['crosstalk', 'zoneCenterErrorMm', 'predictedViewingDistanceMm', 'driftSourceCells']
  const percentiles = value.percentiles
  const distributions = value.distributions
  if (!isRecord(percentiles) || !percentileKeys.every((key) => isPercentiles(percentiles[key]))) return false
  if (!isRecord(distributions) || !percentileKeys.every((key) => isFiniteArray(distributions[key]))) return false
  const crosstalk = distributions.crosstalk as number[]
  const zoneCenterErrorMm = distributions.zoneCenterErrorMm as number[]
  const predictedViewingDistanceMm = distributions.predictedViewingDistanceMm as number[]
  const driftSourceCells = distributions.driftSourceCells as number[]
  if (crosstalk.length !== value.validTrials || zoneCenterErrorMm.length !== value.validTrials ||
    driftSourceCells.length !== value.validTrials || predictedViewingDistanceMm.length > value.validTrials) return false
  if (![crosstalk, zoneCenterErrorMm].every((series) => series.every((item) => item >= 0))) return false
  if (!percentileKeys.every((key) =>
    percentilesMatch(percentiles[key], distributions[key] as number[]),
  )) return false
  const rankingValid = Array.isArray(value.parameterRanking) && value.parameterRanking.every((row) =>
    isRecord(row) && typeof row.parameter === 'string' &&
    isFiniteNumber(row.spearmanRho) && row.spearmanRho >= -1 && row.spearmanRho <= 1 &&
    typeof row.association === 'string',
  )
  return isInteger(value.seed) && isStringArray(value.assumptions) && rankingValid &&
    isFiniteNumber(value.elapsedMs) && value.elapsedMs >= 0 && isJsonData(value)
}

export function isCalibrationDataset(value: unknown): value is CalibrationDataset {
  if (!isRecord(value) || typeof value.name !== 'string') return false
  const xMm = value.xMm
  const viewNames = value.viewNames
  const intensities = value.intensities
  if (!isFiniteArray(xMm) || xMm.length < 3 || !isStrictlyIncreasing(xMm)) return false
  if (!isStringArray(viewNames) || !viewNames.length ||
    viewNames.some((name) => !name.trim()) || new Set(viewNames).size !== viewNames.length) return false
  if (!Array.isArray(intensities) || intensities.length !== viewNames.length) return false
  return intensities.every((series) =>
    isNonnegativeFiniteArray(series) && series.length === xMm.length,
  )
}

function isCalibrationFit(value: unknown): value is CalibrationFit {
  if (!isRecord(value) || value.model !== 'measured or calibration-fitted result') return false
  if (value.estimator !== 'primary-lobe-half-max-centroid-v1') return false
  if (!isFiniteArray(value.measuredCentersMm) || !Array.isArray(value.measuredFwhmMm)) return false
  if (!value.measuredFwhmMm.every((item) => isNullableFinite(item) && (item === null || item >= 0)) ||
    !isFiniteArray(value.measuredCrosstalk) ||
    !value.measuredCrosstalk.every((item) => item >= 0 && item <= 1)) return false
  return isNullableFinite(value.fittedReducedGapMm) && isNullableFinite(value.fittedZoneSpacingMm) &&
    isNullableFinite(value.fittedLensPhaseMm) && isNullableFinite(value.centerFitSlope) &&
    isNullableFinite(value.centerFitIntercept) && isNullableFinite(value.rmsCenterResidualMm) &&
    (value.fittedReducedGapMm === null || value.fittedReducedGapMm > 0) &&
    (value.fittedZoneSpacingMm === null || value.fittedZoneSpacingMm > 0) &&
    (value.rmsCenterResidualMm === null || value.rmsCenterResidualMm >= 0) &&
    isStringArray(value.warnings) && isJsonData(value)
}

function calibrationFitMatchesDataset(
  fit: CalibrationFit,
  inputFingerprint: string,
  dataset: CalibrationDataset,
): boolean {
  const contract = parseFingerprint(inputFingerprint)
  if (!contract || !isRecord(contract.view) || !isPositiveInteger(contract.view.views)) return false
  if (!isCalibrationDataset(contract.calibrationDataset)) return false
  if (calibrationDatasetContentFingerprint(contract.calibrationDataset) !==
    calibrationDatasetContentFingerprint(dataset)) return false
  const viewCount = dataset.viewNames.length
  if (fit.measuredCentersMm.length !== viewCount || fit.measuredFwhmMm.length !== viewCount) return false
  if (fit.measuredCrosstalk.length !== 0 && fit.measuredCrosstalk.length !== viewCount) return false
  const fittedValues = [fit.fittedReducedGapMm, fit.fittedZoneSpacingMm, fit.fittedLensPhaseMm]
  if (!fittedValues.every((item) => item === null) && !fittedValues.every(isFiniteNumber)) return false
  if (fittedValues.every(isFiniteNumber) && (fit.centerFitSlope === null || fit.centerFitSlope >= 0)) return false
  if (contract.view.views !== viewCount && [
    fit.fittedReducedGapMm,
    fit.fittedZoneSpacingMm,
    fit.fittedLensPhaseMm,
    fit.centerFitSlope,
    fit.centerFitIntercept,
    fit.rmsCenterResidualMm,
  ].some((item) => item !== null)) return false
  return true
}

const isFingerprint = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0

export function calibrationDatasetContentFingerprint(dataset: CalibrationDataset): string {
  return JSON.stringify(dataset)
}

export function calibrationFitInputFingerprint(
  state: AppState,
  dataset: CalibrationDataset | null,
): string {
  return JSON.stringify({
    lcd: state.lcd,
    layers: state.layers,
    view: state.view,
    lens: state.lens,
    calibrationDataset: dataset,
  })
}

export function createProjectDocument(snapshot: ProjectSnapshot): ProjectDocumentV1 {
  const calibrationDataset = snapshot.calibrationDataset
    ? {
        dataset: snapshot.calibrationDataset,
        contentFingerprint: calibrationDatasetContentFingerprint(snapshot.calibrationDataset),
      }
    : null
  const calibrationFit = snapshot.calibrationFit && snapshot.calibrationFitFingerprint && calibrationDataset
    ? {
        result: snapshot.calibrationFit,
        inputFingerprint: snapshot.calibrationFitFingerprint,
        datasetFingerprint: calibrationDataset.contentFingerprint,
      }
    : null
  return structuredClone({
    schema: PROJECT_SCHEMA,
    version: PROJECT_VERSION,
    savedAt: new Date().toISOString(),
    state: snapshot.state,
    evidence: {
      simulation: snapshot.simulation,
      tolerance: snapshot.tolerance,
      calibrationDataset,
      calibrationFit,
    },
  })
}

export function emptyProjectDocument(state: AppState = defaultState): ProjectDocumentV1 {
  return createProjectDocument({
    state: structuredClone(state),
    simulation: null,
    tolerance: null,
    calibrationDataset: null,
    calibrationFit: null,
    calibrationFitFingerprint: null,
  })
}

export function parseProjectDocumentValue(value: unknown): ProjectParseResult {
  const errors: string[] = []
  if (!isRecord(value)) return { project: null, errors: ['Project must be a JSON object.'] }
  if (value.schema !== PROJECT_SCHEMA || value.version !== PROJECT_VERSION) {
    return { project: null, errors: ['Unsupported project schema or version.'] }
  }
  if (typeof value.savedAt !== 'string' || !Number.isFinite(Date.parse(value.savedAt))) {
    errors.push('Project savedAt must be a valid timestamp.')
  }
  errors.push(...validateAppState(value.state))
  if (!isRecord(value.evidence)) {
    errors.push('Project evidence must be an object.')
    return { project: null, errors }
  }

  const simulation = value.evidence.simulation
  if (simulation !== null && (
    !isRecord(simulation) || !isFingerprint(simulation.inputFingerprint) ||
    !isSimulationResult(simulation.result, simulation.inputFingerprint)
  )) errors.push('Project simulation evidence is invalid.')

  const tolerance = value.evidence.tolerance
  if (tolerance !== null && (
    !isRecord(tolerance) || !isFingerprint(tolerance.inputFingerprint) ||
    !isMonteCarloResult(tolerance.result, tolerance.inputFingerprint)
  )) errors.push('Project tolerance evidence is invalid.')

  const calibrationDataset = value.evidence.calibrationDataset
  if (calibrationDataset !== null) {
    if (!isRecord(calibrationDataset) || !isCalibrationDataset(calibrationDataset.dataset) ||
      !isFingerprint(calibrationDataset.contentFingerprint)) {
      errors.push('Project calibration dataset evidence is invalid.')
    } else if (
      calibrationDataset.contentFingerprint !== calibrationDatasetContentFingerprint(calibrationDataset.dataset)
    ) {
      errors.push('Project calibration dataset fingerprint does not match its content.')
    }
  }

  const calibrationFit = value.evidence.calibrationFit
  if (calibrationFit !== null) {
    if (!isRecord(calibrationFit) || !isCalibrationFit(calibrationFit.result) ||
      !isFingerprint(calibrationFit.inputFingerprint) || !isFingerprint(calibrationFit.datasetFingerprint)) {
      errors.push('Project calibration fit evidence is invalid.')
    } else if (!isRecord(calibrationDataset) || !isCalibrationDataset(calibrationDataset.dataset)) {
      errors.push('Project calibration fit requires its calibration dataset.')
    } else if (calibrationFit.datasetFingerprint !== calibrationDataset.contentFingerprint) {
      errors.push('Project calibration fit is bound to a different calibration dataset.')
    } else if (!calibrationFitMatchesDataset(
      calibrationFit.result,
      calibrationFit.inputFingerprint,
      calibrationDataset.dataset,
    )) {
      errors.push('Project calibration fit dimensions do not match its dataset.')
    }
  }

  if (errors.length) return { project: null, errors }
  return { project: structuredClone(value as unknown as ProjectDocumentV1), errors: [] }
}

function recoverStateFromDamagedEvidence(value: unknown): ProjectDocumentV1 | null {
  if (!isRecord(value) || value.schema !== PROJECT_SCHEMA || value.version !== PROJECT_VERSION) return null
  if (validateAppState(value.state).length) return null
  const recovered = emptyProjectDocument(structuredClone(value.state as AppState))
  if (typeof value.savedAt === 'string' && Number.isFinite(Date.parse(value.savedAt))) {
    recovered.savedAt = value.savedAt
  }
  return recovered
}

export async function loadStoredProject(
  backend: ProjectStorageBackend,
  legacyStorage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage,
): Promise<ProjectLoadResult> {
  const stored = await backend.read()
  if (stored !== null) {
    const parsed = parseProjectDocumentValue(stored)
    if (parsed.project) {
      return { project: parsed.project, source: 'indexeddb', warning: null, writable: true }
    }
    const recovered = recoverStateFromDamagedEvidence(stored)
    if (recovered) {
      return {
        project: recovered,
        source: 'indexeddb',
        warning: `Stored evidence was discarded because it failed validation: ${parsed.errors.join(' ')}`,
        writable: true,
      }
    }
    return {
      project: emptyProjectDocument(),
      source: 'default',
      warning: `${parsed.errors.join(' ')} IndexedDB was left unchanged; import a valid project to resume persistence.`,
      writable: false,
    }
  }

  const legacyText = legacyStorage?.getItem(LEGACY_STATE_KEY)
  if (!legacyText) {
    return { project: emptyProjectDocument(), source: 'default', warning: null, writable: true }
  }
  try {
    const legacyState = JSON.parse(legacyText) as unknown
    const errors = validateAppState(legacyState)
    if (errors.length) {
      return {
        project: emptyProjectDocument(),
        source: 'default',
        warning: `Legacy input state was ignored: ${errors.join(' ')}`,
        writable: true,
      }
    }
    return {
      project: emptyProjectDocument(structuredClone(legacyState as AppState)),
      source: 'legacy',
      warning: 'Legacy input state was migrated. Prior versions did not store result evidence.',
      writable: true,
    }
  } catch {
    return {
      project: emptyProjectDocument(),
      source: 'default',
      warning: 'Legacy input state was not valid JSON and was ignored.',
      writable: true,
    }
  }
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)
    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(STORE_NAME)) database.createObjectStore(STORE_NAME)
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('Could not open project storage.'))
    request.onblocked = () => reject(new Error('Project storage upgrade is blocked by another tab.'))
  })
}

export function createIndexedDbProjectBackend(): ProjectStorageBackend {
  let databasePromise: Promise<IDBDatabase> | null = null
  const database = () => {
    databasePromise ??= openDatabase()
    return databasePromise
  }
  return {
    async read() {
      const db = await database()
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readonly')
        const request = transaction.objectStore(STORE_NAME).get(CURRENT_PROJECT_KEY)
        request.onsuccess = () => resolve(request.result ?? null)
        request.onerror = () => reject(request.error ?? new Error('Could not read the saved project.'))
      })
    },
    async write(project) {
      const db = await database()
      await new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readwrite')
        transaction.objectStore(STORE_NAME).put(structuredClone(project), CURRENT_PROJECT_KEY)
        transaction.oncomplete = () => resolve()
        transaction.onabort = () => reject(transaction.error ?? new Error('Project save was aborted.'))
        transaction.onerror = () => reject(transaction.error ?? new Error('Could not save the project.'))
      })
    },
  }
}

interface PendingSave {
  sequence: number
  project: ProjectDocumentV1
}

interface SaveWaiter {
  sequence: number
  resolve: () => void
  reject: (error: unknown) => void
}

export class LatestProjectSaveQueue {
  private sequence = 0
  private pending: PendingSave | null = null
  private waiters: SaveWaiter[] = []
  private running = false

  constructor(private readonly backend: ProjectStorageBackend) {}

  enqueue(project: ProjectDocumentV1): Promise<void> {
    const sequence = ++this.sequence
    this.pending = { sequence, project: structuredClone(project) }
    const promise = new Promise<void>((resolve, reject) => {
      this.waiters.push({ sequence, resolve, reject })
    })
    void this.drain()
    return promise
  }

  private settleThrough(sequence: number, error?: unknown): void {
    const settled = this.waiters.filter((waiter) => waiter.sequence <= sequence)
    this.waiters = this.waiters.filter((waiter) => waiter.sequence > sequence)
    settled.forEach((waiter) => error === undefined ? waiter.resolve() : waiter.reject(error))
  }

  private async drain(): Promise<void> {
    if (this.running) return
    this.running = true
    while (this.pending) {
      const current = this.pending
      this.pending = null
      try {
        await this.backend.write(current.project)
        this.settleThrough(current.sequence)
      } catch (error) {
        this.settleThrough(current.sequence, error)
      }
    }
    this.running = false
  }
}

export function projectHasEvidence(project: ProjectDocumentV1): boolean {
  return Object.values(project.evidence).some((record) => record !== null)
}

export function currentProjectFingerprints(state: AppState, dataset: CalibrationDataset | null) {
  const simulation = simulationInputFingerprint(state)
  return {
    simulation,
    tolerance: toleranceInputFingerprint(state, simulation),
    calibration: calibrationFitInputFingerprint(state, dataset),
  }
}
